import { useState, useEffect, useMemo } from "react";
import { supabase } from "../lib/supabase";
import { coalesceRpc } from "../lib/rpcCoalescer";
import { logger } from "../lib/logger";
import { CACHE_VERSION } from "../lib/cacheVersion";
import { toOrgStartISO, toOrgEndISO } from "../lib/timezone";
import { useData } from "./useData";

// =============================================================================
// OperationsKpis — canonical KPI primitive extracted from useOperationsAnalytics
// Consumed by both the admin Operations Dashboard and the Client Portal.
// Date math via toOrgStartISO/EndISO (same helpers admin uses — proven correct
// via May 2 SQL probe: admin params $273,737 == screenshot exact match).
// Does NOT depend on leaderboard / dmStats / prev-period data — only reads
// currentOrganization.timezone from DataContext.
// =============================================================================

export interface OperationsKpis {
  cashCollected: number;
  revenueClosed: number;
  highTicketCash: number;
  lowTicketCash: number;
  // R64: low-ticket DOWNSELL cash (SCO paid_in_full < $2k, has a closer). Shown
  // combined with lowTicketCash in the "Low Ticket" surface, split on hover.
  scoLowTicketCash: number;
  installmentsCash: number;
  dealsClosed: number;
  callsBooked: number;
  preCallsMade: number;
  htDeals: number;
  htRevenue: number;
  ltDeals: number;
  ltRevenue: number;
  noShows: number; // SSOT (metricMeta.ts:259): SCO no_show + orphan pre-calls
  rescheduled: number;
  lost: number;
  installmentsCount: number;
  orphanRate: number; // Orphan pre-calls / Pre-calls × 100 (Mako ruling 2026-04-26)
}

export const ZERO_OPERATIONS_KPIS: OperationsKpis = {
  cashCollected: 0,
  revenueClosed: 0,
  highTicketCash: 0,
  lowTicketCash: 0,
  scoLowTicketCash: 0,
  installmentsCash: 0,
  dealsClosed: 0,
  callsBooked: 0,
  preCallsMade: 0,
  htDeals: 0,
  htRevenue: 0,
  ltDeals: 0,
  ltRevenue: 0,
  noShows: 0,
  rescheduled: 0,
  lost: 0,
  installmentsCount: 0,
  orphanRate: 0,
};

// --- Cache Configuration ---
const HISTORICAL_TTL = 24 * 60 * 60 * 1000; // 24h — historical ranges don't change
const LIVE_TTL = 10 * 60 * 1000; // 10min — today's data changes frequently

interface CacheEntry {
  data: OperationsKpis;
  timestamp: number;
}

interface DateRange {
  start: Date | null;
  end: Date | null;
}

function getCacheTTL(endISO: string): number {
  // Historical if dateRange.end is before yesterday (today - 1 day)
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  yesterday.setHours(23, 59, 59, 999);
  const endDate = new Date(endISO);
  return endDate < yesterday ? HISTORICAL_TTL : LIVE_TTL;
}

function readCache(key: string, endISO: string): OperationsKpis | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const entry: CacheEntry = JSON.parse(raw);
    const ttl = getCacheTTL(endISO);
    if (Date.now() - entry.timestamp > ttl) {
      localStorage.removeItem(key);
      return null;
    }
    return entry.data;
  } catch {
    return null;
  }
}

function writeCache(key: string, data: OperationsKpis): void {
  try {
    const entry: CacheEntry = { data, timestamp: Date.now() };
    localStorage.setItem(key, JSON.stringify(entry));
  } catch {
    // Quota exceeded — non-fatal
  }
}

// =============================================================================
// useOperationsKpis
// =============================================================================
export function useOperationsKpis(
  orgId: string | null,
  dateRange: DateRange
): { kpis: OperationsKpis; isLoading: boolean; error: Error | null } {
  const { currentOrganization } = useData();
  const orgTz = currentOrganization?.timezone;

  // Stable ISO strings — prevent infinite re-render from Date object identity churn
  const startISO = useMemo(
    () => (dateRange.start && orgTz ? toOrgStartISO(dateRange.start, orgTz) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dateRange.start?.getTime(), orgTz]
  );
  const endISO = useMemo(
    () => (dateRange.end && orgTz ? toOrgEndISO(dateRange.end, orgTz) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dateRange.end?.getTime(), orgTz]
  );

  const [kpis, setKpis] = useState<OperationsKpis>(ZERO_OPERATIONS_KPIS);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Edge case: missing required params — return zeros, no RPC call
    if (!orgId || !orgTz || !dateRange.start || !dateRange.end || !startISO || !endISO) {
      setKpis(ZERO_OPERATIONS_KPIS);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const cacheKey = `altara_${CACHE_VERSION}_ops_kpis_${orgId}_${startISO}_${endISO}`;

    // Stale-while-revalidate: show cached data immediately, refresh in background
    const cached = readCache(cacheKey, endISO);
    if (cached) {
      setKpis(cached);
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }

    const fetchKpis = async () => {
      if (!supabase) return;

      try {
        const { data, error: rpcError } = await coalesceRpc("get_operations_kpis", {
          p_organization_id: orgId,
          p_start_date: startISO,
          p_end_date: endISO,
        });

        if (cancelled) return;
        if (rpcError) throw rpcError;

        // RPC returns jsonb — unwrap the single row
        const rows = data ?? [];
        const r = Array.isArray(rows) ? rows[0] : rows;

        // Map snake_case RPC fields → camelCase OperationsKpis
        // noShows = SCO no_show + orphan pre-calls (SSOT metricMeta.ts:259,
        // mirrors useOperationsAnalytics.ts:799 + usePortalKpis.ts:263)
        const fresh: OperationsKpis = {
          cashCollected: Number(r?.cash_collected ?? 0),
          revenueClosed: Number(r?.revenue_closed ?? 0),
          highTicketCash: Number(r?.ht_cash ?? 0),
          lowTicketCash: Number(r?.lt_cash ?? 0),
          scoLowTicketCash: Number(r?.sco_low_ticket_cash ?? 0),
          installmentsCash: Number(r?.installments_cash ?? 0),
          dealsClosed: Number(r?.deals_closed ?? 0),
          callsBooked: Number(r?.calls_booked ?? 0),
          preCallsMade: Number(r?.pre_calls_made ?? 0),
          htDeals: Number(r?.ht_deals ?? 0),
          htRevenue: Number(r?.ht_revenue ?? 0),
          ltDeals: Number(r?.lt_deals ?? 0),
          ltRevenue: Number(r?.lt_revenue ?? 0),
          noShows: Number(r?.no_shows ?? 0) + Number(r?.orphan_pre_calls ?? 0),
          rescheduled: Number(r?.rescheduled ?? 0),
          lost: Number(r?.lost ?? 0),
          installmentsCount: Number(r?.installments_count ?? 0),
          orphanRate: Number(r?.orphan_rate ?? 0),
        };

        if (!cancelled) {
          setKpis(fresh);
          setError(null);
          writeCache(cacheKey, fresh);
        }
      } catch (e) {
        if (!cancelled) {
          const err = e instanceof Error ? e : new Error(String(e));
          logger.error("useOperationsKpis failed:", e);
          setError(err);
          // Leave kpis at last-known-good (either cached or ZERO from init)
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchKpis();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, orgTz, startISO, endISO]);

  return { kpis, isLoading, error };
}
